import { useState, useEffect } from 'react';
import { formatRevenue, formatGrowth, calculatePercentage } from '../utils';

/**
 * Hook para buscar e formatar dados de desempenho
 * @param {string} salonId - ID do salão
 * @param {string} dateRange - Período de datas (today, thisWeek, thisMonth, etc)
 * @returns {Object} - Objeto com métricas, agendamentos, estados de loading e erro
 */
const usePerformanceData = (salonId, dateRange) => {
  const [metrics, setMetrics] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Efeito para carregar os dados
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Em um cenário real, aqui seria uma chamada API
        // const response = await fetch(`/api/performance/${salonId}?range=${dateRange}`);
        // if (!response.ok) throw new Error('Falha ao buscar dados de desempenho');
        // const data = await response.json();
        
        // Simulando um delay para mostrar o loading
        await new Promise(resolve => setTimeout(resolve, 1200));
        
        // Dados simulados
        const simulatedData = generateMockData(dateRange);
        
        setMetrics(simulatedData.metrics);
        setAppointments(simulatedData.appointments);
        setError(null);
      } catch (err) {
        console.error('Erro ao buscar dados de desempenho:', err);
        setError('Não foi possível carregar os dados. Tente novamente mais tarde.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [salonId, dateRange]);

  return { metrics, appointments, loading, error };
};

/**
 * Gera dados simulados para testes
 * @param {string} dateRange - Período selecionado
 * @returns {Object} - Objeto com métricas e agendamentos simulados
 */
const generateMockData = (dateRange) => {
  // Valores base que serão multiplicados por diferentes fatores
  // dependendo do período selecionado
  const baseAppointments = 150;
  const baseRevenue = 12000;
  
  // Fator multiplicador dependendo do período
  let multiplier = 1;
  switch(dateRange) {
    case 'today':
      multiplier = 0.05;
      break;
    case 'thisWeek':
      multiplier = 0.25;
      break;
    case 'last3Months':
      multiplier = 3;
      break;
    case 'thisYear':
      multiplier = 12;
      break;
    case 'thisMonth':
    default:
      multiplier = 1;
  }
  
  // Calcular valores para o período atual
  const totalAppointments = Math.round(baseAppointments * multiplier);
  const totalRevenue = Math.round(baseRevenue * multiplier);
  
  // Calcular valores para o período anterior (ligeiramente menor para simular crescimento)
  const previousAppointments = Math.round(totalAppointments * 0.9);
  const previousRevenue = Math.round(totalRevenue * 0.88);
  
  // Calcular taxas de conclusão, cancelamento e no-show
  const completionRate = 75 + Math.floor(Math.random() * 10);
  const cancellationRate = Math.floor(Math.random() * 15) + 5;
  const noShowRate = 100 - completionRate - cancellationRate;
  
  // Calcular mudanças percentuais em relação ao período anterior
  const appointmentsChange = calculatePercentage(totalAppointments - previousAppointments, previousAppointments);
  const revenueChange = calculatePercentage(totalRevenue - previousRevenue, previousRevenue);
  
  // Dados de receita ao longo do tempo
  const revenueData = {
    data: [
      { label: 'Jan', value: 1200 },
      { label: 'Fev', value: 1500 },
      { label: 'Mar', value: 1800 },
      { label: 'Abr', value: 2200 },
      { label: 'Mai', value: 2500 },
      { label: 'Jun', value: 2800 }
    ]
  };
  
  // Simular agendamentos
  const generateAppointments = (count) => {
    const statuses = ['completed', 'canceled', 'noShow'];
    const appointments = [];
    
    for (let i = 0; i < count; i++) {
      appointments.push({
        id: `appt-${i}`,
        clientName: `Cliente ${i + 1}`,
        serviceName: `Serviço ${(i % 5) + 1}`,
        date: new Date(Date.now() - (i * 24 * 60 * 60 * 1000)).toLocaleDateString('pt-BR'),
        status: statuses[Math.floor(Math.random() * statuses.length)],
        value: Math.floor(Math.random() * 100) + 50
      });
    }
    
    return appointments;
  };
  
  return {
    metrics: {
      totalAppointments,
      completionRate,
      cancellationRate,
      noShowRate,
      totalRevenue,
      revenueChange,
      appointmentsChange,
      retentionChange: 3,
      completionRateChange: 2,
      avgTicket: totalRevenue / (totalAppointments * (completionRate / 100)),
      clientRetention: 68 + Math.floor(Math.random() * 10),
      revenueData,
      rescheduleRate: 12,
      growthRate: 15
    },
    appointments: generateAppointments(15)
  };
};

export default usePerformanceData;
